import { MatterRowContextMenu } from "@/components/features/matters/MatterRowContextMenu";
import { ClaimTypesCell } from "@/components/features/matters/ClaimTypesCell";
import {
  REPORT_CLASSIFICATION_COLUMN_ALIASES,
  APPROACHING_STATUTES_COLUMN_ANCHORS,
} from "@/lib/utils/reports/report-classification-columns";
import { useReportClassificationLabels } from "@/components/features/reports/use-report-classification-labels";
import { ClaimTypeFilter } from "@/components/features/reports/ClaimTypeFilter";
import { IncidentTypeFilter } from "@/components/features/reports/IncidentTypeFilter";
import { ReportPresetControls } from "@/components/features/reports/ReportPresetControls";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";
import {
  Badge,
  Card,
  CardContent,
  DataTable,
  DataTableColumnHeader,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { MatterStatusSelectItems } from "@/components/ui/FormFields/MatterStatusSelectItems";
import { StatuteConfirmedIcon } from "@/components/ui/Icons/StatuteConfirmedIcon";
import { useUser } from "@/components/UserProvider";
import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { useReportFilterPresets } from "@/hooks/use-report-filter-presets";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query";
import { retrieveApproachingStatutesReportQueryOptions } from "@/lib/data/react-query/reports/reports-approaching-statutes-report";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { REPORT_FILTER_PRESET_TYPES } from "@/lib/data/static/report-filter-preset-types";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import type { ApproachingStatutesReportRow } from "@/lib/data/requests/reports/reports-approaching-statutes-report";
import { formatDate } from "@/lib/utils/format";
import { getStatuteDaysBadgeVariant } from "@/lib/utils/matters/statute-urgency-utils";
import {
  type ReportPresetPreviewFilter,
  normalizePresetStringArray,
  toReportPresetPreviewFilter,
} from "@/lib/utils/report-filter-presets";
import { buildMatterStatusLabelOptions } from "@/lib/utils/reports/report-filter-options";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import startCase from "lodash/startCase";
import { AlertTriangle, CheckCircle2, CircleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_auth/reports/approaching-statutes")({
  component: RouteComponent,
  validateSearch: z.object({
    status: z.string().optional(),
    incidentType: z.string().optional(),
    claimType: z.string().optional(),
    window: z.string().optional(),
  }),
});

const APPROACHING_WINDOW_OPTIONS = [30, 60, 90, 120] as const;
type ApproachingWindowOption = (typeof APPROACHING_WINDOW_OPTIONS)[number];
const DEFAULT_APPROACHING_WINDOW_DAYS = 90;
const APPROACHING_STATUTES_REPORT_TABLE_NAME =
  tanstackTableNames.reportsApproachingStatutes;
const APPROACHING_STATUTES_REPORT_PRESET_COLUMN_IDS = {
  incidentType: "Incident Type",
  claimTypes: "Claim Types",
  confirmation: "SOL Confirmation",
  daysRemaining: "Days Remaining",
  matter: "Matter",
  statuteOfLimitations: "Statute of Limitations",
  status: "Status",
} as const;
const APPROACHING_STATUTES_REPORT_PRESET_COLUMN_KEYS = Object.fromEntries(
  Object.entries(APPROACHING_STATUTES_REPORT_PRESET_COLUMN_IDS).map(
    ([key, value]) => [value, key],
  ),
) as Record<string, keyof typeof APPROACHING_STATUTES_REPORT_PRESET_COLUMN_IDS>;

const parseSearchValues = (value?: string) => {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const serializeSearchValues = (values: string[]) =>
  values.length ? values.join(",") : undefined;

const parseWindowSearchValues = (value?: string) =>
  parseSearchValues(value)
    .map((item) => Number(item))
    .filter((item): item is ApproachingWindowOption =>
      APPROACHING_WINDOW_OPTIONS.includes(item as ApproachingWindowOption),
    );

const serializeSingleWindowValue = (value?: ApproachingWindowOption) =>
  typeof value === "number" ? value.toString() : undefined;

const buildSearchFromPreset = (
  rawFilters: Record<string, unknown>,
): Record<string, string | undefined> => {
  const windows = normalizePresetStringArray(rawFilters.window)
    .map((value) => Number(value))
    .filter((value): value is ApproachingWindowOption =>
      APPROACHING_WINDOW_OPTIONS.includes(value as ApproachingWindowOption),
    );
  const nextWindow = windows[0];

  return {
    incidentType: serializeSearchValues(
      normalizePresetStringArray(rawFilters.incidentTypeIds),
    ),
    claimType: serializeSearchValues(
      normalizePresetStringArray(rawFilters.claimTypeIds),
    ),
    status: serializeSearchValues(
      normalizePresetStringArray(rawFilters.status),
    ),
    window: serializeSingleWindowValue(nextWindow),
  };
};

const formatDaysLabel = (daysRemaining: number | null) => {
  if (daysRemaining === null) {
    return "-";
  }
  if (daysRemaining < 0) {
    return `${Math.abs(daysRemaining)}d past`;
  }
  if (daysRemaining === 0) {
    return "Today";
  }
  return `In ${daysRemaining}d`;
};

const getSolConfirmationVariant = (isConfirmed?: boolean | null) =>
  isConfirmed ? ("green" as const) : ("amber" as const);

const getApproachingStatuteColumns =
  (): ColumnDef<ApproachingStatutesReportRow>[] => [
    {
      accessorFn: (row) => row.matterTitle,
      id: "Matter",
      enableHiding: false,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => row.original.matterTitle,
    },
    {
      accessorFn: (row) =>
        row.incidentTypeLabel ??
        (row.incidentTypeId
          ? `Unnamed incident type (${row.incidentTypeId})`
          : "Unclassified"),
      id: "Incident Type",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
    },
    {
      accessorFn: (row) =>
        row.claimTypes.map((claim) => claim.label).join(", ") || "-",
      id: "Claim Types",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <ClaimTypesCell claimTypes={row.original.claimTypes} />
      ),
    },
    {
      accessorFn: (row) => row.matterStatusLabel,
      id: "Status",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        row.original.matterStatusLabel ? (
          <span className="border-border rounded-full border px-2.5 py-0.5 text-xs font-semibold">
            {row.original.matterStatusLabel}
          </span>
        ) : (
          "-"
        ),
    },
    {
      accessorFn: (row) => row.statuteOfLimitations,
      id: "Statute of Limitations",
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const statuteDate = row.original.statuteOfLimitations;
        if (!statuteDate) {
          return "-";
        }

        return (
          <div className="inline-flex items-center gap-2">
            <span>{formatDate(statuteDate)}</span>
            <StatuteConfirmedIcon
              confirmed={row.original.statuteOfLimitationsConfirmed}
            />
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.statuteOfLimitationsConfirmed,
      id: "SOL Confirmation",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const isConfirmed = row.original.statuteOfLimitationsConfirmed;
        return (
          <Badge variant={getSolConfirmationVariant(isConfirmed)}>
            {isConfirmed ? "Confirmed" : "Needs confirmation"}
          </Badge>
        );
      },
    },
    {
      accessorFn: (row) => row.daysRemaining,
      id: "Days Remaining",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const daysRemaining = row.getValue() as number | null;
        const content = formatDaysLabel(daysRemaining);

        if (content === "-") {
          return content;
        }

        return (
          <Badge variant={getStatuteDaysBadgeVariant(daysRemaining)}>
            {content}
          </Badge>
        );
      },
    },
  ];

function RouteComponent() {
  const { hasPagePermission } = useUser();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const selectedIncidentTypes = useMemo(
    () => parseSearchValues(search.incidentType),
    [search.incidentType],
  );
  const selectedClaimTypes = useMemo(
    () => parseSearchValues(search.claimType),
    [search.claimType],
  );
  const [windowSelectValue, setWindowSelectValue] = useState<
    string | undefined
  >();
  const [statusSelectValue, setStatusSelectValue] = useState<
    string | undefined
  >();

  const canViewReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsApproachingStatutes,
  );
  const canViewMatterDetails = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetail,
  );

  const columns = useMemo(() => getApproachingStatuteColumns(), []);
  const classificationFilters = useReportClassificationLabels(
    selectedIncidentTypes,
    selectedClaimTypes,
    canViewReport,
  );

  const selectedStatuses = useMemo<string[]>(() => {
    return parseSearchValues(search.status);
  }, [search.status]);
  const selectedWindowDays = useMemo<ApproachingWindowOption[]>(() => {
    const rawWindows = parseWindowSearchValues(search.window);
    if (rawWindows.length > 0) {
      return [rawWindows[0]];
    }
    return [DEFAULT_APPROACHING_WINDOW_DAYS];
  }, [search.window]);
  const addUniqueValue = (values: string[], value: string) =>
    values.includes(value) ? values : [...values, value];

  const reportFilters = useMemo(
    () => ({
      filters: {
        incidentTypeIds: selectedIncidentTypes,
        claimTypeIds: selectedClaimTypes,
        matterStatusLabels: selectedStatuses,
        windowDays: selectedWindowDays.at(0) ?? DEFAULT_APPROACHING_WINDOW_DAYS,
      },
    }),
    [
      selectedStatuses,
      selectedWindowDays,
      selectedIncidentTypes,
      selectedClaimTypes,
    ],
  );

  const { data, isLoading, isFetching, error } = useQuery({
    ...retrieveApproachingStatutesReportQueryOptions(reportFilters),
    enabled: canViewReport,
  });
  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewReport,
  });

  const statusOptions = useMemo(() => {
    const activeStatuses = matterSettingsData?.data.statuses.filter((status) =>
      ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS.some(
        (category) => category === status.categoryId,
      ),
    );

    return buildMatterStatusLabelOptions(activeStatuses);
  }, [matterSettingsData?.data.statuses]);

  const rows = data?.data.rows ?? [];
  const summaryCounts = data?.data.metrics ?? {
    confirmed: 0,
    needsConfirmation: 0,
    total: 0,
  };

  const summaryCards = [
    {
      icon: AlertTriangle,
      label: "Matters approaching SOL",
      value: summaryCounts.total,
    },
    {
      icon: CircleAlert,
      label: "Needs SOL confirmation",
      value: summaryCounts.needsConfirmation,
    },
    {
      icon: CheckCircle2,
      label: "SOL confirmed",
      value: summaryCounts.confirmed,
    },
  ];

  const approachingStatutesPresetColumnKeysById = useMemo(
    () =>
      columns.reduce<Record<string, string>>((acc, column) => {
        if (typeof column.id !== "string") return acc;
        if (!(column.id in APPROACHING_STATUTES_REPORT_PRESET_COLUMN_KEYS)) {
          return acc;
        }
        acc[column.id] =
          APPROACHING_STATUTES_REPORT_PRESET_COLUMN_KEYS[column.id];
        return acc;
      }, {}),
    [columns],
  );

  const {
    applyPresetByValue,
    clearSelectedPreset,
    createReportFilterPresetMutation,
    deletePresetByValue,
    deleteReportFilterPresetMutation,
    handleColumnOrderChange,
    handleColumnVisibilityChange,
    isReportFilterPresetsLoading,
    isSavePresetOpen,
    presetDescription,
    presetName,
    presetOptions,
    presetSelectValue,
    savePreset,
    savePreviewColumns,
    setIsSavePresetOpen,
    setPresetDescription,
    setPresetName,
    tableRenderKey,
  } = useReportFilterPresets({
    availableColumnIds: columns
      .map((column) => column.id)
      .filter((columnId): columnId is string => typeof columnId === "string"),
    buildSearchFromPreset,
    currentFilterOptions: {
      incidentTypeIds: selectedIncidentTypes,
      claimTypeIds: selectedClaimTypes,
      status: selectedStatuses,
      window: selectedWindowDays.map((value) => value.toString()),
    },
    enabled: canViewReport,
    lockedVisibleColumnIds: [
      APPROACHING_STATUTES_REPORT_PRESET_COLUMN_IDS.matter,
    ],
    onApplySearch: async (nextSearch) => {
      await navigate({
        search: () => nextSearch,
        replace: true,
      });
    },
    presetKeyByColumnId: approachingStatutesPresetColumnKeysById,
    reportType: REPORT_FILTER_PRESET_TYPES.approachingStatutes,
    tableName: APPROACHING_STATUTES_REPORT_TABLE_NAME,
  });
  const getApproachingStatutesCsvValue = (
    row: ApproachingStatutesReportRow,
    columnId: string,
  ) => {
    switch (columnId) {
      case "Incident Type":
        return (
          row.incidentTypeLabel ??
          (row.incidentTypeId
            ? `Unnamed incident type (${row.incidentTypeId})`
            : "Unclassified")
        );
      case "Claim Types":
        return row.claimTypes.map((claim) => claim.label).join(", ") || "-";
      case "Matter":
        return row.matterTitle;
      case "Status":
        return row.matterStatusLabel;
      case "Statute of Limitations":
        return row.statuteOfLimitations
          ? formatDate(row.statuteOfLimitations)
          : "-";
      case "SOL Confirmation":
        return row.statuteOfLimitationsConfirmed
          ? "Confirmed"
          : "Needs confirmation";
      case "Days Remaining":
        return formatDaysLabel(row.daysRemaining);
      default:
        return "";
    }
  };
  const presetPreviewFilters = useMemo(() => {
    const filters: ReportPresetPreviewFilter[] = classificationFilters.map(
      (filter) => toReportPresetPreviewFilter(filter.label),
    );

    selectedWindowDays.forEach((windowDays) => {
      filters.push(
        toReportPresetPreviewFilter(`Window: Next ${windowDays} days`),
      );
    });
    selectedStatuses.forEach((status) => {
      filters.push(toReportPresetPreviewFilter(`Status: ${startCase(status)}`));
    });

    return filters;
  }, [selectedStatuses, selectedWindowDays, classificationFilters]);

  if (!canViewReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout
      title="Approaching statutes report"
      headerActions={
        <ReportPresetControls
          isDeleting={deleteReportFilterPresetMutation.isPending}
          isPresetListLoading={isReportFilterPresetsLoading}
          isSaveOpen={isSavePresetOpen}
          isSaving={createReportFilterPresetMutation.isPending}
          onDeletePresetValue={(value) => {
            void deletePresetByValue(value);
          }}
          onPresetDescriptionChange={setPresetDescription}
          onPresetNameChange={setPresetName}
          onPresetValueChange={(value) => {
            void applyPresetByValue(value);
          }}
          onSave={() => {
            void savePreset();
          }}
          onSaveOpenChange={setIsSavePresetOpen}
          presetDescription={presetDescription}
          presetName={presetName}
          presetOptions={presetOptions}
          presetValue={presetSelectValue}
          savePreviewColumns={savePreviewColumns}
          savePreviewFilters={presetPreviewFilters}
          saveTitle="Save approaching statutes report preset"
          selectId="approaching-statutes-report-preset-select"
        />
      }
    >
      <ReportFilterBar
        appliedFilters={[
          ...classificationFilters.map((filter) => ({
            key: `${filter.dimension}:${filter.value}`,
            label: filter.label,
            onRemove: () => {
              clearSelectedPreset();
              const values =
                filter.dimension === "incidentType"
                  ? selectedIncidentTypes
                  : selectedClaimTypes;
              void navigate({
                search: (prev) => ({
                  ...prev,
                  [filter.dimension]: serializeSearchValues(
                    values.filter((value) => value !== filter.value),
                  ),
                }),
                replace: true,
              });
            },
          })),
          ...selectedWindowDays.map((windowDays) => ({
            key: `window-${windowDays}`,
            label: `Next ${windowDays} days`,
            onRemove: () => {
              clearSelectedPreset();
              void navigate({
                search: (prev) => ({
                  ...prev,
                  window: serializeSingleWindowValue(undefined),
                }),
                replace: true,
              });
            },
          })),
          ...selectedStatuses.map((status) => ({
            key: `status-${status}`,
            label: startCase(status),
            onRemove: () => {
              clearSelectedPreset();
              const nextStatuses = selectedStatuses.filter(
                (item) => item !== status,
              );
              void navigate({
                search: (prev) => ({
                  ...prev,
                  status: serializeSearchValues(nextStatuses),
                }),
                replace: true,
              });
            },
          })),
        ]}
        onClearFilters={async () => {
          clearSelectedPreset();
          setWindowSelectValue(undefined);
          setStatusSelectValue(undefined);
          await navigate({
            search: () => ({
              status: serializeSearchValues([]),
              window: serializeSingleWindowValue(undefined),
            }),
            replace: true,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <IncidentTypeFilter
            value={selectedIncidentTypes}
            onChange={(values) => {
              clearSelectedPreset();
              void navigate({
                search: (prev) => ({
                  ...prev,
                  incidentType: serializeSearchValues(values),
                }),
                replace: true,
              });
            }}
          />
          <ClaimTypeFilter
            value={selectedClaimTypes}
            onChange={(values) => {
              clearSelectedPreset();
              void navigate({
                search: (prev) => ({
                  ...prev,
                  claimType: serializeSearchValues(values),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={windowSelectValue ?? ""}
            onValueChange={async (value) => {
              const nextWindow = Number(value) as ApproachingWindowOption;
              setWindowSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  window: serializeSingleWindowValue(nextWindow),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger
              className="h-11 w-[220px]"
              aria-label="Select timeframe"
            >
              <SelectValue placeholder="Select timeframe" />
            </SelectTrigger>
            <SelectContent>
              {APPROACHING_WINDOW_OPTIONS.map((option) => (
                <SelectItem key={option} value={option.toString()}>
                  Next {option} days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusSelectValue ?? ""}
            onValueChange={async (value) => {
              const nextStatuses = addUniqueValue(selectedStatuses, value);
              setStatusSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  status: serializeSearchValues(nextStatuses),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger
              className="h-11 w-[220px]"
              aria-label="Select status"
            >
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <MatterStatusSelectItems items={statusOptions} />
            </SelectContent>
          </Select>
        </div>
      </ReportFilterBar>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label} className="border-border/60 shadow-sm">
              <CardContent className="space-y-2 pt-4">
                {isLoading || isFetching ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted h-10 w-10 animate-pulse rounded-full" />
                      <div className="bg-muted h-4 w-36 animate-pulse rounded" />
                    </div>
                    <div className="bg-muted h-7 w-20 animate-pulse rounded" />
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted text-foreground flex h-10 w-10 items-center justify-center rounded-full">
                        <Icon className="h-5 w-5" />
                      </div>
                      <Text variant="subHeading">{card.label}</Text>
                    </div>
                    <Text variant="title" className="text-2xl leading-8">
                      {card.value.toLocaleString()}
                    </Text>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      <DataTable
        entityLabelPlural="matters"
        columnIdAliases={REPORT_CLASSIFICATION_COLUMN_ALIASES}
        newColumnAnchors={APPROACHING_STATUTES_COLUMN_ANCHORS}
        key={tableRenderKey}
        hideSearchBar
        columns={columns}
        data={rows}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        initialSort={{ id: "Statute of Limitations", desc: false }}
        renderRowContextMenu={(row, trigger) => {
          const matterId = row.original.matterId;
          return canViewMatterDetails && matterId ? (
            <MatterRowContextMenu matterId={matterId} trigger={trigger} />
          ) : (
            trigger
          );
        }}
        onRowClick={async (row) => {
          if (!canViewMatterDetails || !row.original.matterId) {
            return;
          }
          await navigate({
            to: "/matters/$matterId",
            params: { matterId: row.original.matterId },
          });
        }}
        tableName={APPROACHING_STATUTES_REPORT_TABLE_NAME}
        isLoading={isLoading || isFetching}
        isError={Boolean(error)}
        noResultsMessage={
          selectedWindowDays.length === 1
            ? `No statutes within the next ${selectedWindowDays[0]} days.`
            : "No statutes found for the selected filters."
        }
        leftToolbarContent={
          <Text variant="body" className="text-lg font-semibold">
            Matters per statute deadline
          </Text>
        }
        exportCsv={{
          fileName: "approaching-statutes-report.csv",
          getCellValue: getApproachingStatutesCsvValue,
        }}
      />
    </ReportsPageLayout>
  );
}
